import * as XLSX from "xlsx-js-style";

const HEADERS = [
  "Nombre",
  "Email",
  "DNI",
  "Fecha inscripción",
  "Inscripción congreso",
  "Estado inscripción",
  "Plan congreso",
];

const LAST_COL = HEADERS.length - 1;

const COL_WIDTHS = [
  { wch: 22 },
  { wch: 36 },
  { wch: 14 },
  { wch: 18 },
  { wch: 16 },
  { wch: 18 },
  { wch: 24 },
];

const BORDER_GRID = {
  top: { style: "thin", color: { rgb: "BFBFBF" } },
  bottom: { style: "thin", color: { rgb: "BFBFBF" } },
  left: { style: "thin", color: { rgb: "BFBFBF" } },
  right: { style: "thin", color: { rgb: "BFBFBF" } },
};

const ESTADO_LABELS = {
  completado: "Completado",
  pendiente: "Pendiente",
  fallido: "Fallido",
  cancelado: "Cancelado",
};

function str(v) {
  if (v == null || v === undefined) return "";
  return String(v).trim();
}

function formatDetailDate(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
}

function inscripcionEstadoLabel(estado) {
  return ESTADO_LABELS[estado] || str(estado);
}

function rowFromTallerInscrito(row) {
  const ins = row.inscripcionCongreso;
  const hasIns = !!row.inscripcionCongresoId;
  return [
    str(row.nombre),
    str(row.email),
    str(row.dni),
    formatDetailDate(row.createdAt),
    hasIns ? "Sí" : "No",
    hasIns ? inscripcionEstadoLabel(ins?.estado) : "",
    hasIns ? str(ins?.planTituloSnapshot) || str(ins?.planId) : "",
  ];
}

function tallerSectionTitle(tal) {
  const n = (tal.inscritos || []).length;
  const meta = [tal.diaEtiqueta, tal.hora, tal.sala].filter(Boolean).join(" · ");
  const titulo = tal.titulo || "Taller";
  const suffix = tal.orphan ? " (eliminado del programa)" : "";
  const metaPart = meta ? ` — ${meta}` : "";
  return `${titulo}${suffix}${metaPart} (${n} apuntado${n === 1 ? "" : "s"})`;
}

function dataColumnWrapText(col) {
  return [0, 1, 6].includes(col);
}

function applySheetVisuals(ws, sections) {
  ws["!cols"] = COL_WIDTHS;

  const merges = [];
  const rowHeights = [];

  const planStyle = {
    font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "E97132" } },
    alignment: { horizontal: "left", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  const headerStyle = {
    font: { bold: true, sz: 11, color: { rgb: "1F1F1F" } },
    fill: { patternType: "solid", fgColor: { rgb: "D6DCE5" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  for (const sec of sections) {
    merges.push({
      s: { r: sec.planRow, c: 0 },
      e: { r: sec.planRow, c: LAST_COL },
    });
    rowHeights[sec.planRow] = { hpt: 28 };

    const planRef = XLSX.utils.encode_cell({ r: sec.planRow, c: 0 });
    const planCell = ws[planRef];
    if (planCell) {
      planCell.s = { ...planStyle };
    }

    for (let c = 0; c <= LAST_COL; c++) {
      const ref = XLSX.utils.encode_cell({ r: sec.headerRow, c });
      const cell = ws[ref];
      if (!cell) continue;
      cell.s = { ...headerStyle };
    }

    let zebra = false;
    if (sec.dataEnd >= sec.dataStart) {
      for (let r = sec.dataStart; r <= sec.dataEnd; r++) {
        rowHeights[r] = { hpt: 22 };
        for (let c = 0; c <= LAST_COL; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          const cell = ws[ref];
          if (!cell) continue;
          const wrap = dataColumnWrapText(c);
          const zebraFill = zebra
            ? { patternType: "solid", fgColor: { rgb: "F5F6F8" } }
            : { patternType: "solid", fgColor: { rgb: "FFFFFF" } };

          cell.s = {
            font: { sz: 11, color: { rgb: "222222" } },
            fill: zebraFill,
            alignment: {
              vertical: "top",
              wrapText: wrap,
              horizontal: c === 4 ? "center" : "left",
            },
            border: BORDER_GRID,
          };
        }
        zebra = !zebra;
      }
    }
  }

  if (merges.length) ws["!merges"] = merges;
  ws["!rows"] = rowHeights;
}

/**
 * @param {Array<{ titulo?: string, diaEtiqueta?: string, hora?: string, sala?: string, orphan?: boolean, inscritos?: object[] }>} tallerRows
 * @returns {import('xlsx').WorkSheet|null}
 */
export function buildTalleresInscripcionesWorksheet(tallerRows) {
  const groups = (tallerRows || []).filter(
    (tal) => Array.isArray(tal.inscritos) && tal.inscritos.length > 0
  );
  if (groups.length === 0) return null;

  const aoa = [];
  /** @type {{ planRow: number, headerRow: number, dataStart: number, dataEnd: number }[]} */
  const sections = [];
  let rowIndex = 0;

  for (const tal of groups) {
    const planRow = rowIndex;
    aoa.push([tallerSectionTitle(tal)]);
    rowIndex += 1;

    const headerRow = rowIndex;
    aoa.push(HEADERS);
    rowIndex += 1;

    const dataStart = rowIndex;
    for (const row of tal.inscritos) {
      aoa.push(rowFromTallerInscrito(row));
      rowIndex += 1;
    }
    const dataEnd = rowIndex - 1;

    sections.push({
      planRow,
      headerRow,
      dataStart,
      dataEnd: dataEnd >= dataStart ? dataEnd : dataStart - 1,
    });

    aoa.push([]);
    rowIndex += 1;
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applySheetVisuals(ws, sections);
  return ws;
}

function excelDateStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

function slugForFilename(raw) {
  const s = String(raw || "taller")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return (s || "taller").slice(0, 40);
}

/**
 * @param {{ year?: number|string, tallerRows: object[] }} params
 */
export function exportCongresoTalleresInscripcionesExcel({ year, tallerRows }) {
  const ws = buildTalleresInscripcionesWorksheet(tallerRows);
  if (!ws) return false;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Talleres");

  const y = year != null && year !== "" ? String(year) : "edicion";
  XLSX.writeFile(wb, `talleres-congreso-${y}-${excelDateStamp()}.xlsx`);
  return true;
}

/**
 * Exporta un único taller (misma hoja/estilo que la exportación global).
 * @param {{ year?: number|string, taller: object }} params
 */
export function exportCongresoTallerInscripcionesExcel({ year, taller }) {
  if (!taller || !Array.isArray(taller.inscritos) || taller.inscritos.length === 0) {
    return false;
  }
  const ws = buildTalleresInscripcionesWorksheet([taller]);
  if (!ws) return false;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Taller");

  const y = year != null && year !== "" ? String(year) : "edicion";
  const slug = slugForFilename(taller.titulo || taller.tallerId);
  XLSX.writeFile(wb, `taller-${slug}-congreso-${y}-${excelDateStamp()}.xlsx`);
  return true;
}
